import { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Eye, ShoppingCart, X, Sparkles, CheckCircle, Download, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';
import { cloudFunctions } from '@/lib/firebase';
import { Button } from '@/components/ui/Button';
import { LoadingScreen } from '@/components/ui/LoadingScreen';
import { GlassCard } from '@/components/ui/GlassCard';

interface SitePage {
  slug: string;
  title: string;
  navLabel: string;
  html: string;
}

interface PreviewData {
  name: string;
  templateType: string;
  previewHtml: string | null;
  status: string;
  price?: number; // Price in cents
  sitePages?: string | null;
  siteMeta?: { siteType?: string; sectionsUsed?: string[] } | null;
}

interface CustomizationStatus {
  customizationComplete: boolean;
  buyerContactInfo: Record<string, unknown> | null;
}

type DeployPlatform = 'netlify' | 'vercel';

/**
 * For multi-page sites: swap the <main> content and update the active nav link
 * in the previewHtml document shell to show a different page.
 */
function buildPageDocument(previewHtml: string, page: SitePage): string {
  // Replace <main>...</main> content
  const mainOpenMatch = previewHtml.match(/<main\b[^>]*>/i);
  if (!mainOpenMatch) return previewHtml;

  const mainOpenIdx = previewHtml.indexOf(mainOpenMatch[0]);
  const mainCloseIdx = previewHtml.lastIndexOf('</main>');
  if (mainOpenIdx === -1 || mainCloseIdx === -1) return previewHtml;

  // Extract the main opening tag and the style from the original page
  const originalMainContent = previewHtml.slice(
    mainOpenIdx + mainOpenMatch[0].length,
    mainCloseIdx
  );

  // Extract <style> blocks from original (shared styles)
  const sharedStyles: string[] = [];
  originalMainContent.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, (match) => {
    sharedStyles.push(match);
    return '';
  });

  // Extract the new page's main content (inside its <main> tag)
  const pageMainMatch = page.html.match(/<main\b[^>]*>([\s\S]*)<\/main>/i);
  const pageContent = pageMainMatch ? pageMainMatch[1] : page.html;

  // Check if page content already has styles
  const hasOwnStyles = /<style\b/i.test(pageContent);

  // Build new main content: shared styles + page content
  const newMainContent = (!hasOwnStyles ? sharedStyles.join('\n') + '\n' : '') + pageContent;

  let doc = previewHtml.slice(0, mainOpenIdx + mainOpenMatch[0].length)
    + newMainContent
    + previewHtml.slice(mainCloseIdx);

  // Update <title>
  doc = doc.replace(/<title>[^<]*<\/title>/i, `<title>${page.title.replace(/</g, '&lt;')}</title>`);

  // Update active nav link: remove existing .active, add to current page
  doc = doc.replace(/ class="active"/g, '');
  const pageHref = page.slug === 'index' ? 'index.html' : `${page.slug}.html`;
  doc = doc.replace(
    new RegExp(`<a href="${pageHref.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`, 'g'),
    `<a href="${pageHref}" class="active"`
  );

  return doc;
}

export function PreviewSitePage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [preview, setPreview] = useState<PreviewData | null>(null);
  const [customizationStatus, setCustomizationStatus] = useState<CustomizationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDeployPanel, setShowDeployPanel] = useState(false);
  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [deployPlatform, setDeployPlatform] = useState<DeployPlatform>('netlify');
  const [currentPageSlug, setCurrentPageSlug] = useState('index');

  // Check for purchase=cancelled in URL
  const purchaseCancelled = searchParams.get('purchase') === 'cancelled';

  useEffect(() => {
    async function fetchPreview() {
      if (!token) {
        setError('Invalid preview link');
        setLoading(false);
        return;
      }

      try {
        const data = await cloudFunctions.getPreviewByToken({ token });
        if (process.env.NODE_ENV === 'development') {
          console.log('[PreviewPage] Fetched data:', {
            name: data.name,
            hasPreviewHtml: !!data.previewHtml,
            previewHtmlLength: data.previewHtml?.length || 0,
            status: data.status,
            isMultiPage: !!data.sitePages,
          });
        }
        setPreview(data);

        // If sold, check customization status
        if (data.status === 'sold') {
          const status = await cloudFunctions.getSiteCustomizationStatus({ token });
          setCustomizationStatus({
            customizationComplete: status.customizationComplete,
            buyerContactInfo: status.buyerContactInfo,
          });
        }
      } catch (err) {
        console.error('[PreviewPage] Error fetching preview:', err);
        setError('Preview not found or has expired');
      } finally {
        setLoading(false);
      }
    }

    fetchPreview();
  }, [token]);

  useEffect(() => {
    if (purchaseCancelled) {
      toast.info('Purchase cancelled. You can try again when ready.');
    }
  }, [purchaseCancelled]);

  const handlePurchase = async () => {
    if (!token) return;

    setPurchaseLoading(true);
    try {
      const { sessionUrl } = await cloudFunctions.createSitePurchaseCheckout({
        token,
      });

      if (sessionUrl) {
        window.location.href = sessionUrl;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      toast.error('Failed to start checkout. Please try again.');
      setPurchaseLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!token) return;

    setDownloadLoading(true);
    try {
      const { fileName, zipBase64 } = await cloudFunctions.downloadSiteZip({ token });

      // Convert base64 to blob and download
      const byteCharacters = atob(zipBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/zip' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success('Download started!');
    } catch (err) {
      toast.error('Failed to download. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  // Parse multi-page data
  const pages: SitePage[] | null = useMemo(() => {
    if (!preview?.sitePages) return null;
    try {
      const parsed = typeof preview.sitePages === 'string'
        ? JSON.parse(preview.sitePages)
        : preview.sitePages;
      return Array.isArray(parsed) && parsed.length > 1 ? parsed : null;
    } catch {
      return null;
    }
  }, [preview?.sitePages]);

  const isMultiPage = !!pages && pages.length > 1;

  const rawPreviewHtml = preview?.previewHtml ?? null;

  // For multi-page: generate the current page's document
  const displayHtml = useMemo(() => {
    if (!rawPreviewHtml) return null;
    if (!isMultiPage || currentPageSlug === 'index') return rawPreviewHtml;

    const currentPage = pages?.find(p => p.slug === currentPageSlug);
    if (!currentPage) return rawPreviewHtml;

    return buildPageDocument(rawPreviewHtml, currentPage);
  }, [rawPreviewHtml, isMultiPage, currentPageSlug, pages]);

  // Intercept navigation clicks in the iframe for multi-page sites
  useEffect(() => {
    if (!isMultiPage) return;

    function handleMessage(event: MessageEvent) {
      if (event.data?.type === 'navigate' && event.data?.slug) {
        setCurrentPageSlug(event.data.slug);
      }
    }
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isMultiPage]);

  // Inject navigation interception script into iframe content
  const iframeHtml = useMemo(() => {
    if (!displayHtml) return displayHtml;

    // Script handles both multi-page (.html) links and single-page hash links
    const navScript = `<script>
document.addEventListener('click', function(e) {
  var link = e.target.closest('a');
  if (!link) return;
  var href = link.getAttribute('href');
  if (!href) return;
  // Multi-page navigation
  var match = href.match(/^([\\w-]+)\\.html$/);
  if (match) {
    e.preventDefault();
    window.parent.postMessage({ type: 'navigate', slug: match[1] }, '*');
    return;
  }
  // Hash link scrolling (single-page sites)
  if (href.startsWith('#')) {
    e.preventDefault();
    var target = document.querySelector(href);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  }
});
<\/script>`;

    return displayHtml.replace('</body>', navScript + '</body>');
  }, [displayHtml, isMultiPage]);

  const hasValidContent = iframeHtml && iframeHtml.trim().length > 100 && /<(section|main|body|html)/i.test(iframeHtml);

  // --- Early returns AFTER all hooks ---

  if (loading) {
    return <LoadingScreen message="Loading your website made with Romulus..." />;
  }

  if (error || !preview) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-navy-deep">
        <GlassCard className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-white mb-4">Preview Not Found</h1>
          <p className="text-gray-400 mb-6">
            This preview link may have expired or is invalid.
          </p>
          <Button asChild>
            <Link to="/">Go Home</Link>
          </Button>
        </GlassCard>
      </div>
    );
  }

  const { name, templateType, status } = preview;
  const isPurchased = status === 'sold';
  const isCustomized = customizationStatus?.customizationComplete ?? false;

  // If we have pre-rendered preview HTML, show it in a sandboxed iframe
  if (hasValidContent) {
    return (
      <div className="min-h-screen bg-navy-deep">
        {/* Header bar for preview info */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-navy-900/95 backdrop-blur-xl border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-primary-400">
                <Eye className="w-5 h-5" />
                <span className="font-semibold">Preview</span>
              </div>
              <div>
                <p className="text-sm font-medium text-white">{name}</p>
                <p className="text-xs text-gray-500">
                  {isMultiPage ? `Multi-page Website (${pages!.length} pages)` : 'Website Preview'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Multi-page page switcher */}
              {isMultiPage && pages && (
                <div className="flex gap-1 bg-white/5 rounded-lg p-1">
                  {pages.map(page => (
                    <button
                      key={page.slug}
                      onClick={() => setCurrentPageSlug(page.slug)}
                      className={`px-3 py-1 rounded text-xs font-medium transition-all ${
                        currentPageSlug === page.slug
                          ? 'bg-primary-500/20 text-primary-400'
                          : 'text-gray-400 hover:text-gray-200'
                      }`}
                    >
                      {page.navLabel}
                    </button>
                  ))}
                </div>
              )}
              <span className="text-xs text-gray-500 bg-white/5 px-2 py-1 rounded">
                {templateType}
              </span>
              {isPurchased ? (
                <>
                  <span className="text-xs px-2 py-1 rounded text-primary-400 bg-primary-500/10 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Purchased
                  </span>
                  <button
                    onClick={() => setShowDeployPanel(!showDeployPanel)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                    style={{
                      background: 'linear-gradient(135deg, #B08080, #B08080)',
                    }}
                  >
                    <Download className="w-4 h-4" />
                    Deploy
                    {showDeployPanel ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowPurchaseModal(true)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
                  style={{
                    background: 'linear-gradient(135deg, #B08080, #c49494)',
                  }}
                >
                  <ShoppingCart className="w-4 h-4" />
                  Purchase Site
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Deploy Panel (for purchased sites) */}
        {isPurchased && showDeployPanel && (
          <div className="fixed top-16 right-0 z-40 w-full max-w-md m-4 rounded-2xl overflow-hidden shadow-2xl" style={{ background: 'rgba(20,20,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="p-6">
              <h2 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-primary-400" />
                Deploy Your Website
              </h2>

              {/* Customization Required Banner */}
              {!isCustomized && (
                <div className="mb-6 p-4 rounded-xl bg-[#B08080]/10 border border-[#B08080]/30">
                  <p className="text-sm text-gray-300 mb-3">
                    Before downloading, please customize your site with your contact information.
                  </p>
                  <Link
                    to={`/purchase/success?token=${token}&session_id=returning`}
                    className="block w-full py-2 rounded-lg text-center text-white font-semibold text-sm"
                    style={{ background: 'linear-gradient(135deg, #B08080, #c49494)' }}
                  >
                    Complete Customization →
                  </Link>
                </div>
              )}

              {/* Download Button */}
              <button
                onClick={handleDownload}
                disabled={downloadLoading || !isCustomized}
                className="w-full mb-6 py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all"
                style={{
                  background: !isCustomized ? 'rgba(255,255,255,0.05)' : (downloadLoading ? 'rgba(255,255,255,0.1)' : 'linear-gradient(135deg, #B08080, #c49494)'),
                  cursor: !isCustomized ? 'not-allowed' : (downloadLoading ? 'wait' : 'pointer'),
                  opacity: !isCustomized ? 0.5 : 1,
                }}
              >
                {downloadLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Preparing Download...
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5" />
                    {isCustomized
                      ? `Download Website (ZIP${isMultiPage ? ` - ${pages!.length} pages` : ''})`
                      : 'Complete Customization First'}
                  </>
                )}
              </button>

              {/* Platform Tabs */}
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setDeployPlatform('netlify')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                    deployPlatform === 'netlify'
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'bg-white/5 text-gray-400 border border-transparent'
                  }`}
                >
                  Netlify
                </button>
                <button
                  onClick={() => setDeployPlatform('vercel')}
                  className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                    deployPlatform === 'vercel'
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'bg-white/5 text-gray-400 border border-transparent'
                  }`}
                >
                  Vercel
                </button>
              </div>

              {/* Deploy Instructions */}
              <div className="bg-white/5 rounded-xl p-4 mb-4">
                {deployPlatform === 'netlify' ? (
                  <ol className="space-y-3 text-sm text-gray-300">
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                      <span>Go to <a href="https://app.netlify.com" target="_blank" rel="noopener noreferrer" className="text-primary-400 underline">app.netlify.com</a> and sign up (free)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                      <span>Click "Add new site" → "Deploy manually"</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                      <span>Drag and drop the downloaded ZIP file</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">✓</span>
                      <span className="text-primary-400">Your site is live!</span>
                    </li>
                  </ol>
                ) : (
                  <ol className="space-y-3 text-sm text-gray-300">
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</span>
                      <span>Go to <a href="https://vercel.com" target="_blank" rel="noopener noreferrer" className="text-primary-400 underline">vercel.com</a> and sign up (free)</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</span>
                      <span>Click "Add New" → "Project"</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</span>
                      <span>Select "Upload" and upload the ZIP contents</span>
                    </li>
                    <li className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-primary-500/20 text-primary-400 flex items-center justify-center flex-shrink-0 text-xs font-bold">✓</span>
                      <span className="text-primary-400">Your site is live!</span>
                    </li>
                  </ol>
                )}
              </div>

              {/* Quick links */}
              <div className="flex gap-2">
                <a
                  href={deployPlatform === 'netlify' ? 'https://app.netlify.com' : 'https://vercel.com'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 py-2 px-4 rounded-lg bg-white/5 text-gray-300 text-sm font-medium text-center flex items-center justify-center gap-2 hover:bg-white/10 transition-all"
                >
                  Open {deployPlatform === 'netlify' ? 'Netlify' : 'Vercel'}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Iframe for preview display - allow scripts for interactive content */}
        <div className="pt-16">
          <iframe
            srcDoc={iframeHtml || undefined}
            className="w-full border-0"
            style={{ height: 'calc(100vh - 4rem)' }}
            title="Site Preview"
            sandbox="allow-scripts"
          />
        </div>

        {/* Purchase Modal */}
        {showPurchaseModal && !isPurchased && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)' }}
            onClick={(e) => e.target === e.currentTarget && setShowPurchaseModal(false)}
          >
            <div
              className="w-full max-w-md rounded-2xl p-6"
              style={{
                background: 'rgba(20,20,30,0.98)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}
                  >
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Purchase Website</h2>
                    <p className="text-xs text-gray-400">One-time payment</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPurchaseModal(false)}
                  className="p-2 rounded-lg hover:bg-white/10 text-gray-400"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mb-6 p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.05)' }}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Website</span>
                  <span className="text-white font-medium">{name}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-400">Template</span>
                  <span className="text-white font-medium">{templateType}</span>
                </div>
                {isMultiPage && (
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400">Pages</span>
                    <span className="text-white font-medium">{pages!.length} pages</span>
                  </div>
                )}
                <div className="border-t border-white/10 my-3"></div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Total</span>
                  <span className="text-2xl font-bold text-white">${((preview.price || 29900) / 100).toFixed(0)}</span>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3 text-sm">
                  <CheckCircle className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">Full ownership of all website files</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <Download className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">Download as ZIP for any hosting provider</span>
                </div>
                <div className="flex items-start gap-3 text-sm">
                  <Sparkles className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300">Step-by-step deployment guide included</span>
                </div>
              </div>

              <button
                onClick={handlePurchase}
                disabled={purchaseLoading}
                className="w-full py-3 rounded-xl text-white font-semibold flex items-center justify-center gap-2 transition-all"
                style={{
                  background: purchaseLoading
                    ? 'rgba(255,255,255,0.1)'
                    : 'linear-gradient(135deg, #10b981, #059669)',
                  cursor: purchaseLoading ? 'wait' : 'pointer',
                }}
              >
                {purchaseLoading ? (
                  <>
                    <div
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-5 h-5" />
                    Proceed to Checkout
                  </>
                )}
              </button>

              <p className="text-xs text-center text-gray-500 mt-4">
                Secure payment powered by Stripe
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback: no previewHtml yet - show a message
  return (
    <div className="min-h-screen flex items-center justify-center bg-navy-deep">
      <GlassCard className="text-center max-w-md">
        <h1 className="text-2xl font-bold text-white mb-4">Preview Not Ready</h1>
        <p className="text-gray-400 mb-2">
          This site preview hasn't been generated yet.
        </p>
        <p className="text-gray-500 text-sm mb-6">
          {rawPreviewHtml ? '(Content appears to be corrupted - please try regenerating the site)' : 'The site may still be generating or the link may have expired.'}
        </p>
        <Button asChild>
          <Link to="/">Go Home</Link>
        </Button>
      </GlassCard>
    </div>
  );
}
